import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/store/useApp";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { signupAccount, toAccount } from "@/lib/authApi";
import { homeRouteFor } from "@/lib/types";

export default function Signup() {
  const navigate = useNavigate();
  const account = useApp((s) => s.account);
  const authStatus = useApp((s) => s.authStatus);
  const signup = useApp((s) => s.signup);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (authStatus !== "ready" || !account) return;
    navigate(homeRouteFor(account), { replace: true });
  }, [account, authStatus, navigate]);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Lê do formulário (não só do estado): autofill/gerenciador de senha pode preencher o campo
    // sem disparar onChange, deixando o estado vazio e barrando o cadastro como "campo vazio".
    const form = e.currentTarget;
    const emailValue = ((form.elements.namedItem("email") as HTMLInputElement | null)?.value ?? email)
      .trim()
      .toLowerCase();
    const passwordValue = (form.elements.namedItem("password") as HTMLInputElement | null)?.value ?? password;
    const confirmValue = (form.elements.namedItem("confirm") as HTMLInputElement | null)?.value ?? confirm;

    if (!emailValue || !passwordValue) return toast.error("Preencha todos os campos");
    if (passwordValue !== confirmValue) return toast.error("As senhas não coincidem");
    if (passwordValue.length < 6) return toast.error("A senha precisa ter no mínimo 6 caracteres");
    setIsSubmitting(true);

    try {
      const response = await signupAccount({
        email: emailValue,
        password: passwordValue,
      });
      const account = toAccount(response);
      signup(account);
      toast.success("Conta criada! Vamos completar sua ficha comercial.");
      navigate(homeRouteFor(account));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Nao foi possivel criar sua conta"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    "Catálogo com +130 itens hortifrúti",
    "Configuração de corte, unidade e janela",
    "Tabela de preços dedicada por cliente",
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden">
            <Logo />
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-3xl font-bold tracking-tight">Criar conta</h2>
            <p className="text-sm text-muted-foreground">
              É grátis. Você completa o cadastro comercial logo após.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email corporativo</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" name="email" type="email" autoComplete="username" placeholder="voce@empresa.com.br" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" name="password" type="password" autoComplete="new-password" placeholder="mínimo 6 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirmar senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="confirm" name="confirm" type="password" autoComplete="new-password" placeholder="repita a senha" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-9 h-11" />
              </div>
            </div>

            <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Criando conta..." : "Criar conta"}
              {!isSubmitting && <ArrowRight className="h-4 w-4" />}
            </Button>
          </form>

          <div className="text-sm text-muted-foreground text-center">
            Já tem conta?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline">
              Entrar
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex relative hero-gradient overflow-hidden flex-col justify-between p-12 text-primary-foreground order-1 lg:order-2">
        <Logo className="text-primary-foreground" />
        <div className="relative z-10 max-w-md space-y-8">
          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
            Sua próxima rotina de compras começa aqui.
          </h1>
          <ul className="space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-foreground/15 border border-primary-foreground/20">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-primary-foreground/90">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 text-xs text-primary-foreground/60">
          Após criar conta, sua ficha comercial fica em análise. O catálogo já libera de imediato.
        </p>
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary-foreground/5 blur-3xl" />
      </div>
    </div>
  );
}
